import { post } from "./client";
import { type Nominal, type OkResponse, url } from "./common";
import type { ShowroomEntryListItem } from "./showroom";
import type { WorkshopSettings } from "./workshop";

export type PublicShowroomId = Nominal<string, "PublicShowroom">;

// ---- Entity ----

export type PublicShowroom = {
  id: PublicShowroomId;
  title: string;
  slug: string;
  description: string | null;
  createdAt: Date;
  flags: string[];
  // Backend schema: jsonb NOT NULL default '{}'. No code path writes specific
  // keys and no consumer reads them — kept as a loose object until a real
  // shape emerges.
  data: Record<string, unknown>;
};

// Minimal subset used by populated relations (e.g. WorkshopInfo.publicShowroom).
export type PublicShowroomSummary = Pick<
  PublicShowroom,
  "id" | "slug" | "title"
>;

// Returned by GET /public-showroom/:slug — composite shape with entries and workshop settings.
export type PublicShowroomInfo = {
  id: PublicShowroomId;
  title: string;
  description: string | null;
  data: Record<string, unknown>;
  entries: ShowroomEntryListItem[];
  workshopSettings: WorkshopSettings;
};

// ---- Models ----

export const publicShowroomModels = {
  PUBLIC_SHOWOROOMS: url<PublicShowroom[]>(`/public-showrooms`),
  PUBLIC_SHOWROOM_INFO: (slug: string) =>
    url<PublicShowroomInfo>(`/public-showroom/${slug}`),
};

// ---- Mutations ----

type CreatePublicShowroomInput = {
  title: string;
  slug: string;
  description?: string | null;
};

type UpdatePublicShowroomInput = {
  id: PublicShowroomId;
  title: string;
  slug: string;
  description?: string | null;
};

export const publicShowroomMutations = {
  createPublicShowroom: ({
    title,
    slug,
    description,
  }: CreatePublicShowroomInput) =>
    post<PublicShowroom>(
      "/public-showrooms/create",
      { title, slug, description },
      [publicShowroomModels.PUBLIC_SHOWOROOMS],
    ),
  updatePublicShowroom: ({
    id,
    title,
    slug,
    description,
  }: UpdatePublicShowroomInput) =>
    post<OkResponse>(
      `/public-showrooms/${id}/update`,
      { title, slug, description },
      [publicShowroomModels.PUBLIC_SHOWOROOMS],
    ),
  deletePublicShowroom: ({ id }: { id: PublicShowroomId }) =>
    post<OkResponse>(`/public-showrooms/${id}/delete`, {}, [
      publicShowroomModels.PUBLIC_SHOWOROOMS,
    ]),
};
